/* eslint-disable @typescript-eslint/naming-convention, camelcase */
// snake_case and `proposal[...]` keys throughout are Rails parameter names, sent
// verbatim in the POST body. Renaming them would break the controller.
import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, TextInput, Checkbox, Loader } from '@ui';
import './SaveAndSendModalStyles.scss';
const FORM_ID = 'proposal-save-and-send-form';
/**
 * Wizard Save and Send dialog.
 *
 * The Quimby Modal supplies the chrome; a real `<form>` inside it does the work. This
 * posts the whole wizard step to `create_new_proposals`, which saves the proposal,
 * sends the email and redirects out of the wizard. It is deliberately a browser form
 * submit rather than AJAX: that endpoint has no JSON path, and putting the proposal
 * save on a fetch it was never written for would risk the save to fix a sizing bug.
 */
export default function SaveAndSendModalView({ action, csrfToken, proposalId, inspectionId, isCreateFlow, currentStep, templateId, sendToEmail, emailSubject, emailBody, pdfPath, labels, isOpen, onClose }) {
    const fields = {
        authenticity_token: csrfToken,
        'proposal[proposal_id]': String(proposalId),
        'proposal[inspection_id]': inspectionId == null ? '' : String(inspectionId),
        'proposal[is_create_flow]': String(isCreateFlow !== null && isCreateFlow !== void 0 ? isCreateFlow : false),
        'proposal[update_statuses_deficiencies]': 'true',
        current_step: currentStep !== null && currentStep !== void 0 ? currentStep : '',
        template_id: templateId == null ? '' : String(templateId)
    };
    // Rails sends JSON `null` for a blank column and a default parameter only catches
    // `undefined`, so these are coerced. A null reaching `.trim()` tears down the island
    // and the dialog silently never opens (IP-15897).
    const [recipient, setRecipient] = useState(sendToEmail !== null && sendToEmail !== void 0 ? sendToEmail : '');
    const [subject, setSubject] = useState(emailSubject !== null && emailSubject !== void 0 ? emailSubject : '');
    const [body, setBody] = useState(emailBody !== null && emailBody !== void 0 ? emailBody : '');
    const [bccMe, setBccMe] = useState(false);
    const [bccMultiple, setBccMultiple] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [previewLoaded, setPreviewLoaded] = useState(false);
    const isValid = recipient.trim().length > 0 && subject.trim().length > 0;
    // The POST goes through a detached form built here, not through the <form> the fields
    // sit in: this endpoint has no JSON path, so it must be a real browser POST carrying
    // the whole wizard step. Same approach as Invoices/QboTaxPrecheck. The visible form
    // exists to give the dialog Enter-to-submit; it delegates straight to this.
    const submit = () => {
        if (!isValid || submitting)
            return;
        setSubmitting(true);
        const form = document.createElement('form');
        form.method = 'post';
        form.action = action;
        form.style.display = 'none';
        const values = Object.assign(Object.assign({}, fields), { 'proposal[send_to_email]': recipient, 'proposal[email_subject]': subject, 'proposal[email_body]': body, 
            // The controller branches on this being present.
            save_and_send: labels.submit });
        if (bccMe)
            values.bcc_me = '1';
        if (bccMultiple)
            values.bcc_multiple_participants = '1';
        Object.entries(values).forEach(([name, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
    };
    return (React.createElement(Modal, { isOpen: isOpen, onClose: onClose, variant: "base", className: "saveAndSendModal" },
        React.createElement(ModalHeader, { title: labels.title, onClose: onClose }),
        React.createElement(ModalBody, { className: "saveAndSendModal__body" },
            React.createElement("form", { id: FORM_ID, className: "saveAndSendModal__form", onSubmit: (e) => {
                    e.preventDefault();
                    submit();
                } },
                React.createElement(TextInput, { id: "proposal-save-send-email", type: "email", multiple: true, label: labels.sendToEmail, required: true, value: recipient, onChange: (e) => setRecipient(e.target.value) }),
                React.createElement(Checkbox, { id: "proposal-save-send-bcc-multiple", label: labels.bccMultipleParticipants, checked: bccMultiple, onChange: setBccMultiple }),
                React.createElement(TextInput, { id: "proposal-save-send-subject", label: labels.emailSubject, required: true, value: subject, onChange: (e) => setSubject(e.target.value) }),
                React.createElement(Checkbox, { id: "proposal-save-send-bcc-me", label: labels.bccMe, checked: bccMe, onChange: setBccMe }),
                React.createElement(TextInput, { id: "proposal-save-send-body", multiline: true, rows: 12, label: labels.emailBody, value: body, onChange: (e) => setBody(e.target.value) })),
            pdfPath && (React.createElement("div", { className: "saveAndSendModal__preview" },
                !previewLoaded && (React.createElement("div", { className: "saveAndSendModal__previewLoading" },
                    React.createElement(Loader, null))),
                React.createElement("object", { data: pdfPath, type: "application/pdf", className: "saveAndSendModal__pdf", onLoad: () => setPreviewLoaded(true) },
                    React.createElement("p", null,
                        React.createElement("a", { href: pdfPath, target: "_blank", rel: "noopener noreferrer" }, labels.pdfNotSupported)))))),
        React.createElement(ModalFooter, null,
            React.createElement(Button, { onClick: onClose, disabled: submitting }, labels.cancel),
            React.createElement(Button, { variant: "primary", type: "submit", form: FORM_ID, disabled: submitting || !isValid }, labels.submit))));
}
